import React from 'react';
import { Box } from '@mui/material';

/**
 * Lowercases `value` one character (code point) at a time and records, for every UTF-16 unit of the
 * result, the source range of the character it came from. Lowercasing can change length ('İ' becomes
 * 'i' + U+0307), so indexes into the folded string are not indexes into the original.
 */
const foldWithSourceMap = (value) => {
  let folded = '';
  const sourceStart = [];
  const sourceEnd = [];
  let position = 0;
  for (const character of value) {
    const lower = character.toLowerCase();
    const end = position + character.length;
    for (let unit = 0; unit < lower.length; unit += 1) {
      sourceStart.push(position);
      sourceEnd.push(end);
    }
    folded += lower;
    position = end;
  }
  return { folded, sourceStart, sourceEnd };
};

/**
 * Highlights every case-insensitive occurrence of the search term in a result label.
 *
 * The term is user input, so it is matched literally with indexOf: building a RegExp from it let a
 * pattern such as `(a+)+$` backtrack on the main thread, threw on input like `(`, and read `.` or `*`
 * as patterns rather than the characters the user typed. Matching happens on the folded text; each
 * match is mapped back to whole characters of the original, so the highlight covers what matched.
 *
 * @param {string} text - The text to highlight
 * @param {string} searchText - The search term to highlight
 * @returns {React.ReactNode} Text with highlighted matches
 */
export const highlightText = (text, searchText) => {
  const source = text === null || text === undefined ? '' : String(text);
  const needle = searchText ? foldWithSourceMap(String(searchText)).folded : '';
  if (!needle || !source) return text;

  const { folded, sourceStart, sourceEnd } = foldWithSourceMap(source);
  const parts = [];
  let from = 0;
  let at = folded.indexOf(needle);
  while (at !== -1) {
    const start = sourceStart[at];
    const end = sourceEnd[at + needle.length - 1];
    // A match beginning inside a character the previous one already covered is part of that highlight.
    if (start >= from) {
      if (start > from) parts.push(source.slice(from, start));
      parts.push(
        <Box component="span" key={start} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {source.slice(start, end)}
        </Box>
      );
      from = end;
    }
    at = folded.indexOf(needle, at + needle.length);
  }
  if (parts.length === 0) return text;
  if (from < source.length) parts.push(source.slice(from));
  return parts;
};
